import React, { useEffect, PropsWithChildren, useContext } from 'react';
import { noop, PropsWithMetadata } from '@highlight-ui/utils-commons';

import { Popover } from '../Popover';
import { SelectContext } from './context/SelectProvider';
import { Padding, SelectVariant } from '../types';
import { extractProps } from '../helpers';
import SelectListBox from './SelectListBox';
import { SelectListBoxOptionProps } from './SelectListBoxOption';

export type SelectPopoverProps = PropsWithChildren<
  PropsWithMetadata<{
    /**
     * Class name to apply to the element
     */
    className?: string;
    /**
     * `inline` when used outside forms and `full-width` when used inside forms.
     */
    variant?: SelectVariant;
    /**
     * Gets called whenever the visibility of the select's list has changed
     */
    onListVisibilityChange?: (visible: boolean) => void;
    /**
     * Additional padding to use when detecting collisions of the popover list of select options
     */
    popoverCollisionPadding?: Padding;
  }>
>;

const SelectPopover = React.forwardRef<HTMLDivElement, SelectPopoverProps>(
  (
    {
      className,
      variant = 'inline',
      metadata,
      onListVisibilityChange = noop,
      children,
      ...restProps
    },
    ref,
  ) => {
    const { isLoading, isVisible, setIsVisible, setOptions } =
      useContext(SelectContext);

    useEffect(() => {
      const basicOptionProps = extractProps<SelectListBoxOptionProps<any>>(
        children,
        (child) => {
          return child.type === SelectListBox.Option;
        },
      );

      setOptions(
        basicOptionProps.map((o) => ({
          label: o.children as string,
          subLabel: o.subLabel as string,
          value: o.value,
          disabled: o.disabled,
        })),
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [children]);

    return (
      <Popover
        isVisible={isVisible}
        className={className}
        variant={variant}
        metadata={metadata}
        onListVisibilityChange={(toggle) => {
          onListVisibilityChange(toggle);
          setIsVisible(toggle);
        }}
        isLoading={isLoading}
        ref={ref}
        {...restProps}
      >
        {children}
      </Popover>
    );
  },
) as React.ForwardRefExoticComponent<
  SelectPopoverProps & React.RefAttributes<HTMLDivElement>
>;

export default SelectPopover;
